import { Injectable, inject } from '@angular/core';
import { Asset, InstrumentType } from '@zero/shared/util-models';
import { addMinutes, addMonths, lastDayOfMonth, parseISO, subDays } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { TradeFacade } from '../trade.facade';

@Injectable({
  providedIn: 'root',
})
export class TradingTimesService {
  public readonly germanTimeZone = 'Europe/Berlin';
  readonly #tradeFacade = inject(TradeFacade);

  public initHolidays() {
    this.#tradeFacade.loadHolidays();
  }

  public isActiveTradingHours(currentDateTime: Date, asset: Asset): boolean {
    const { instrumentType, tradingHoursStartTime, tradingHoursStopTime } = asset;
    if (instrumentType === InstrumentType.Crypto) {
      return true;
    }

    const germanDate = toZonedTime(currentDateTime, this.germanTimeZone);
    if (this.isWeekend(germanDate) || this.isHoliday(germanDate)) {
      return false;
    }

    const transformTradingHours = (hours: string) => Number(`${hours.split(':').join('')}00`);
    const transformTime = (num: number) => num.toString().padStart(2, '0');
    const openingTime = transformTradingHours(tradingHoursStartTime);
    const closingTime = transformTradingHours(tradingHoursStopTime);
    const currentTime = Number(
      transformTime(germanDate.getHours()) + transformTime(germanDate.getMinutes()) + transformTime(germanDate.getSeconds()),
    );

    return currentTime >= openingTime && currentTime <= closingTime;
  }

  public isActiveTradingHoursTradingEndOffset(currentDateTime: Date, asset: Asset, offsetInMinutes: number): boolean {
    const localCurrentDateTime = currentDateTime || this.getCurrentLocalDateTime();
    if (!this.isActiveTradingHours(localCurrentDateTime, asset)) {
      return false;
    }

    const germanDateTime = toZonedTime(localCurrentDateTime, this.germanTimeZone);
    const germanTradingEndWithOffset = addMinutes(
      parseISO(`${germanDateTime.toISOString().slice(0, 10)}T${asset.tradingHoursStopTime}:00.000`),
      offsetInMinutes,
    );

    return germanDateTime <= germanTradingEndWithOffset;
  }

  public isWeekend(germanDateTime: Date): boolean {
    return germanDateTime.getDay() === 6 || germanDateTime.getDay() === 0;
  }

  public getTimeString(dateTime: Date): string {
    const year = dateTime.getFullYear();
    const month = dateTime.getMonth() + 1;
    const day = dateTime.getDate();

    const monthStr = month <= 9 ? `0${month}` : `${month}`;
    const dayStr = day <= 9 ? `0${day}` : `${day}`;

    return `${year}-${monthStr}-${dayStr}`;
  }

  public isHoliday(germanDateTime: Date): boolean {
    return !this.#tradeFacade.holidays?.length || this.#tradeFacade.holidays.includes(this.getTimeString(germanDateTime));
  }

  public getCurrentLocalDateTime(): Date {
    return new Date();
  }

  public getGermanTodayDate(): Date {
    const localDateTime = this.getCurrentLocalDateTime();
    return toZonedTime(localDateTime, this.germanTimeZone);
  }

  public getValidToDatePreset(currentDate: Date, months = 1): Date {
    const currentInGermany = toZonedTime(currentDate, this.germanTimeZone);
    const startOfCurrentMonth: Date = new Date(currentInGermany.getFullYear(), currentInGermany.getMonth(), 1);
    const endOfNextMonthDate = lastDayOfMonth(addMonths(startOfCurrentMonth, months));

    let resultDate = endOfNextMonthDate;
    while (this.isWeekend(resultDate) || (this.#tradeFacade.holidays.length && this.isHoliday(resultDate))) {
      resultDate = subDays(resultDate, 1);
    }

    return resultDate;
  }
}
